import type { FC, MouseEvent, MutableRefObject } from 'react';
import { Picture, Modal } from '@ui';
import LoadingPage from '@components/LoadingPage';
import AddressDeliveryItem from '@components/Layout/Header/components/InfoHeader/components/HeaderDropdown/components/AddressDeliveryItem';
import PickupShopItem from '@components/Layout/Header/components/InfoHeader/components/HeaderDropdown/components/PickupShopItem';
import icons from '@icons';
import {
  selectedItemRed,
  carIDeliveryBlack,
  deliveryTypePickup
} from '@images';
import { observer } from 'mobx-react';
import { useStores } from '@root/src/stores';
import { twcls } from '@utils/twcls';
import type { ShopFragmentMainEndpointFragment } from '@schema/main';
import PriceChangeWarning from '@components/PriceChangeWarning';
import './region-modal.scss';

const {
  bonus: { red: bonusRubleRed },
  close: { midGrey: closeOutlineMidGrey }
} = icons.Outline;

const chooseButton =
  'flex h-11 w-full cursor-pointer items-center justify-center rounded-lg border border-[#EEEEEE] bg-white text-center text-sm font-medium leading-none hover:bg-[#EEEEEE]';

export interface RegionModalProps {
  onClose: () => void;
  isShowPickups: boolean;
  onChangeStore: (param: boolean) => void;
  isLoading: boolean;
  openedShops?: ShopFragmentMainEndpointFragment[];
  onClickAddRegion: () => void;
  onSelectAddress: (address: any) => (isShouldClose?: any) => void;
  openPopupId: number | null;
  showOptionsPopup: boolean;
  togglePopup: (e: MouseEvent, itemId: number) => void;
  changeStateModalEdit: (address: any, e: MouseEvent) => void;
  deleteAddress: (e: MouseEvent, item: any) => void;
  selectPP: (address: any) => (isShouldClose?: any) => void;
  addressOptionsPopupRef: MutableRefObject<HTMLDivElement | null>;
  addressItemRefs: MutableRefObject<Record<number, HTMLDivElement | null>>;
}

const RegionModal: FC<RegionModalProps> = observer(
  ({
    onClose,
    isShowPickups,
    onChangeStore,
    isLoading,
    openedShops,
    onClickAddRegion,
    onSelectAddress,
    openPopupId,
    showOptionsPopup,
    togglePopup,
    changeStateModalEdit,
    deleteAddress,
    selectPP,
    addressOptionsPopupRef,
    addressItemRefs
  }) => {
    const { userStore, configStore, cartStore } = useStores();
    const { moscowAddresses } = userStore;
    const { data } = configStore;
    const cashbackForPickup = data?.cashback_for_pickup;
    const store_id = cartStore?.cart?.store_id;
    const selectedAddress = cartStore?.cart?.peopleAddress?.id;
    return (
      <Modal
        placementDesktop='top'
        placementMobile='fullscreen'
        withoutCross
        onClose={onClose}
      >
        <div className='modal-region'>
          <div className='w-[510px] pl-10 pr-10 pt-8 pb-9 sm:flex sm:h-full sm:w-full sm:flex-col sm:px-4 sm:pt-6 sm:pb-0'>
            <div className='absolute right-3 top-3 z-[1] flex justify-end'>
              <Picture
                icon={closeOutlineMidGrey}
                height='24'
                width='24'
                pictureClassName='cursor-pointer'
                onClick={onClose}
              />
            </div>
            <>
              <div className='hidden sm:mb-4 sm:block sm:text-xl sm:font-medium sm:leading-6'>
                Мои адреса
              </div>
              <div className='flex gap-3 mb-4'>
                <div
                  onClick={() => {
                    onChangeStore(false);
                  }}
                  className={twcls(
                    chooseButton,
                    !isShowPickups && 'bg-[#EEEEEE]'
                  )}
                >
                  <Picture
                    className='mr-3'
                    icon={!isShowPickups ? selectedItemRed : carIDeliveryBlack}
                    height='20'
                    alt=''
                  />
                  Доставка
                </div>
                <div
                  onClick={() => {
                    onChangeStore(true);
                  }}
                  className={twcls(
                    chooseButton,
                    isShowPickups && 'bg-[#EEEEEE]'
                  )}
                >
                  <Picture
                    className='mr-3'
                    icon={isShowPickups ? selectedItemRed : deliveryTypePickup}
                    height='20'
                    alt=''
                  />
                  Самовывоз
                </div>
              </div>

              {isShowPickups && (
                <PriceChangeWarning className='-mt-2 mb-3 rounded-lg' />
              )}
            </>

            {isShowPickups && !!Number(cashbackForPickup) ? (
              <span className='mb-6 flex items-center justify-start gap-2 text-sm font-normal leading-[18px] text-[#B32442]'>
                <Picture icon={bonusRubleRed} height='20' />
                {Number(cashbackForPickup) > 0
                  ? `Вернем ${cashbackForPickup}% бонусами при самовывозе`
                  : ''}
              </span>
            ) : null}

            <div className='list custom-scroll relative mb-2 h-80 max-h-[calc(100vh-136px)] overflow-auto pr-2 sm:h-auto sm:min-h-[100px] sm:flex-1'>
              {isLoading && (
                <LoadingPage fullHeight className='absoluteCenter' />
              )}
              {!isShowPickups
                ? moscowAddresses.map(item => {
                    const isActive = item.id === Number(selectedAddress);
                    return (
                      <AddressDeliveryItem
                        key={item.id}
                        item={item}
                        isActive={isActive}
                        isOptionsOpen={
                          openPopupId === item.id && showOptionsPopup
                        }
                        popupRef={addressOptionsPopupRef}
                        registerRef={el => {
                          if (!item?.id) {
                            return;
                          }
                          addressItemRefs.current[item?.id] = el;
                        }}
                        onSelect={onSelectAddress(item)}
                        onToggleOptions={e => {
                          if (!item?.id) {
                            return;
                          }
                          togglePopup(e, item?.id);
                        }}
                        onEdit={e => {
                          changeStateModalEdit(item, e);
                        }}
                        onDelete={e => {
                          deleteAddress(e, item);
                        }}
                      />
                    );
                  })
                : openedShops?.map(item => (
                    <PickupShopItem
                      key={item.id}
                      item={item}
                      isActive={item.id === store_id}
                      onSelect={selectPP(item)}
                    />
                  ))}
            </div>

            <div className='grid gap-4 sm:mt-auto sm:shrink-0 sm:grid-cols-1 sm:px-6 sm:pt-4 sm:pb-6'>
              <div
                onClick={onClickAddRegion}
                className='w-full cursor-pointer rounded-lg border border-[#cbcbcb] border-solid bg-white px-6 py-2.5 text-center text-sm font-normal leading-[1.2]'
              >
                {isShowPickups ? 'Посмотреть на карте' : '+ Добавить адрес'}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
);

export default RegionModal;
